import { hooks } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormatter, useTranslations } from "next-intl";
import React, { useMemo } from "react";

import { useCartNotification } from "~/contexts/cart-notification";
import { useWishlist } from "~/contexts/wishlist";
import { useCart } from "~/hooks/use-cart";

import Button from "../common/button";
import Dialog from "../common/dialog";
import Icon from "../common/icon";
import styles from "./cart-oos-dialog.module.scss";
import CartProductTile, { CartProductTileContext } from "./cart-product-tile";

type CartOOSDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  outOfStockDeliveryOrderLines: EVA.Core.OrderLineDto[];
};

export default function CartOOSDialog({ isOpen, onClose, outOfStockDeliveryOrderLines }: CartOOSDialogProps) {
  const cart = useCart();
  const queryClient = useQueryClient();
  const t = useTranslations();
  const format = useFormatter();
  const { saveForLater } = useWishlist();
  const { notifyRemoved } = useCartNotification();
  const shoppingCartCancel = hooks.useShoppingCartModify({ service: Core.CancelOrder });

  const outOfStockFromDeliveryLinesProductIDs = useMemo(
    () => outOfStockDeliveryOrderLines.map((line) => line.ProductID),
    [outOfStockDeliveryOrderLines]
  );

  const removeAllOutOfStockProductsForDeliveryFromCart = useMutation({
    mutationFn: async () => {
      // Normalize the lines parameter into an array if it's not already an array
      let arrayedLines = [];
      if (!Array.isArray(outOfStockDeliveryOrderLines)) {
        arrayedLines = [outOfStockDeliveryOrderLines];
      } else {
        arrayedLines = [...outOfStockDeliveryOrderLines?.filter((line) => line !== undefined)];
      }

      // Filter any undefined values from the array (may happen in contexts of OOS products)
      if (!arrayedLines?.length) return null;

      const removedAt = new Date();

      await shoppingCartCancel({
        payload: {
          OrderID: cart?.ShoppingCart.ID,
          OrderLines: arrayedLines.map((line) => ({
            OrderLineID: line.ID,
          })),
        },
      });

      notifyRemoved(removedAt);
    },
  });

  if (removeAllOutOfStockProductsForDeliveryFromCart.isSuccess) {
    queryClient.invalidateQueries(["digitalGiftCardsDetails", cart?.ShoppingCart.ID]);
  }

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <>
          {t.rich("generic.OOSCartDialogTitle", {
            OutOfStockIcon: (chunks) => <Icon name="non-shoppable-country" />,
          })}
        </>
      }
      onClose={() => onClose()}
      customStyles={styles}
    >
      <p className={styles.notallCartProductsAreInStockForDeliveryDescription}>
        {t.rich("generic.OOSCartDialogDescription", {
          oosProductsCount: outOfStockDeliveryOrderLines?.length,
        })}
      </p>
      <div className={styles.oosCartDialogProductContent}>
        {outOfStockDeliveryOrderLines.map(
          (line, idx) =>
            line && (
              <CartProductTile
                key={`outOfStockDeliveryOrderLines-${idx}-${line?.ProductID}`}
                line={line}
                disableCartActions={true}
                index={idx}
                context={CartProductTileContext.oosCartModal}
              />
            )
        )}
      </div>
      <Button
        variant="primary"
        onClick={async () => {
          await saveForLater(outOfStockFromDeliveryLinesProductIDs as number[]);
          await removeAllOutOfStockProductsForDeliveryFromCart.mutateAsync();
          onClose();
        }}
      >
        {t("generic.OOSCartDialogCTA")}
      </Button>
    </Dialog>
  );
}
